import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useNavigate } from 'react-router-dom';
import { Save as SaveIcon } from '@mui/icons-material';
import FirebaseService from '../services/FirebaseService';

const AddVerifiedPersonnel = ({ isDialog = false, personnelToEdit = null, onSuccess = null, onCancel = null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Initialize form data with personnelToEdit if provided
  const [formData, setFormData] = useState(personnelToEdit ? {
    auditor_name: personnelToEdit.auditor_name || '',
    certi_no: personnelToEdit.certi_no || '',
    issue_date: personnelToEdit.issue_date ? new Date(personnelToEdit.issue_date) : null,
    country: personnelToEdit.country || '',
    status: personnelToEdit.status || 'Certified',
    certificate_standard: personnelToEdit.certificate_standard || ''
  } : {
    auditor_name: '',
    certi_no: '',
    issue_date: null,
    country: '',
    status: 'Certified',
    certificate_standard: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      issue_date: date
    });
  };

  const validateForm = () => {
    const requiredFields = ['auditor_name', 'certi_no', 'issue_date', 'country', 'status', 'certificate_standard'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Format the date to ISO string format (YYYY-MM-DD)
      const formattedData = {
        ...formData,
        issue_date: formData.issue_date ? formData.issue_date.toISOString().split('T')[0] : null
      };
      
      if (personnelToEdit) {
        // Update existing personnel
        await FirebaseService.updateVerifiedPersonnel(personnelToEdit.id, formattedData);
      } else {
        // Add new personnel
        await FirebaseService.addVerifiedPersonnel(formattedData);
      }
      
      setSuccess(true);
      
      // If in dialog mode, call onSuccess callback
      if (isDialog && onSuccess) {
        onSuccess();
        return;
      }
      
      // Reset form after successful submission if not in dialog mode
      setFormData({
        auditor_name: '',
        certi_no: '',
        issue_date: null,
        country: '',
        status: 'Certified',
        certificate_standard: ''
      });
      
      // Navigate back to the personnel list after a short delay if not in dialog mode
      setTimeout(() => {
        navigate('/admin/verified-personnel');
      }, 2000);
      
    } catch (err) {
      // Removed console.error
      setError('Failed to add personnel. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    if (isDialog && onCancel) {
      onCancel();
    } else {
      navigate('/admin/verified-personnel');
    }
  };

  // If in dialog mode, render a simplified version
  if (isDialog) {
    return (
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certified Auditor Name"
                name="auditor_name"
                value={formData.auditor_name}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certificate Number"
                name="certi_no"
                value={formData.certi_no}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Issue Date"
                  value={formData.issue_date}
                  onChange={handleDateChange}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      name="issue_date"
                    />
                  }
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="Certified">Certified</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certificate Standard"
                name="certificate_standard"
                value={formData.certificate_standard}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g. OHSAS ISO 45001:2018"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : personnelToEdit ? 'Update Personnel' : 'Save Personnel'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>
    );
  }

  // Regular full-page version
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" href="/">
            Home
          </Link>
          <Link 
            underline="hover" 
            color="inherit" 
            href="/admin/verified-personnel"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/verified-personnel');
            }}
          >
            Verified Personnel
          </Link>
          <Typography color="text.primary">{personnelToEdit ? 'Edit Personnel' : 'Add New Personnel'}</Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" component="h1" gutterBottom>
          {personnelToEdit ? 'Edit Verified Personnel' : 'Add New Verified Personnel'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Personnel added successfully! Redirecting to personnel list...
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Personnel Details
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certified Auditor Name"
                name="auditor_name"
                value={formData.auditor_name}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certificate Number"
                name="certi_no"
                value={formData.certi_no}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Issue Date"
                  value={formData.issue_date}
                  onChange={handleDateChange}
                  renderInput={(params) => 
                    <TextField 
                      {...params} 
                      fullWidth 
                      required 
                      name="issue_date"
                    />
                  }
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="Certified">Certified</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="SUSPENDED">SUSPENDED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Certificate Standard"
                name="certificate_standard"
                value={formData.certificate_standard}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g. OHSAS ISO 45001:2018"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : personnelToEdit ? 'Update Personnel' : 'Save Personnel'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default AddVerifiedPersonnel;
